import React from 'react';
import OverviewRow from './OverviewRow';

import { ReactComponent as WorkerIcon } from '@repo/common/assets/icons/20px/icon_20px_office-worker_outline.svg';
import { ReactComponent as WorkSpaceIcon } from '@repo/common/assets/icons/20px/icon_20px_office-workspace_outline.svg';
import { ReactComponent as CashIcon } from '@repo/common/assets/icons/20px/icon_20px_cash_outline.svg';
import { ReactComponent as BusinessIcon } from '@repo/common/assets/icons/20px/icon_20px_business-brief_version1_outline.svg';
import { ReactComponent as ClockIcon } from '@repo/common/assets/icons/20px/icon_20px_clock_outline.svg';
import { ReactComponent as OfficeIcon } from '@repo/common/assets/icons/20px/icon_20px_office-building_outline.svg';
import { ReactComponent as SkillsIcon } from '@repo/common/assets/icons/20px/icon_20px_skills-outline.svg';
import { ReactComponent as LocationIcon } from '@repo/common/assets/icons/16px/icon_16px_location.svg';
import { ReactComponent as CopyIcon } from '@repo/common/assets/icons/16px/icon_16px_copy.svg';
import {
  getJobOptionCode,
  getJobOptionLabel,
  useLang,
  useT,
} from '@repo/common';

/**
 * Language Skills 한 줄 요약.
 * 한국어를 첫 항목으로 두고 TOPIK 요구는 괄호 안에 병기, 이후 요구 언어를 " | " 로 나열.
 * 예: Korean(Advanced, TOPIK 5) | English | German
 * 상세 충족 여부는 Compare requirements 에서 판단하므로 여기선 요약만.
 */
export const buildLanguageSkills = (job, optionMeta, lang, t) => {
  const label = (id, fallback = '') =>
    getJobOptionLabel(optionMeta, id, fallback, lang);
  const code = (id) => getJobOptionCode(optionMeta, id);

  const koreanDetails = [];
  const koreanCode = code(job?.koreanSkillLevelOptionItemId);
  const koreanLabel = label(job?.koreanSkillLevelOptionItemId);
  if (koreanCode === 'NOT_REQUIRED') {
    koreanDetails.push(t('Not required', '무관'));
  } else if (koreanLabel) {
    koreanDetails.push(koreanLabel);
  }

  const topikCode = code(job?.topikRequirementOptionItemId);
  if (topikCode === 'MIN_LEVEL') {
    const level = label(job?.topikRequirementLevelOptionItemId);
    const num = (level.match(/\d/) || [])[0];
    koreanDetails.push(
      num ? `TOPIK ${num}` : t('TOPIK required', 'TOPIK 필수'),
    );
  } else if (topikCode === 'REQUIRED') {
    koreanDetails.push(t('TOPIK required', 'TOPIK 필수'));
  } else if (topikCode === 'PREFERRED') {
    koreanDetails.push(t('TOPIK preferred', 'TOPIK 우대'));
  }

  const speakingCode = code(job?.topikSpeakingRequirementOptionItemId);
  if (speakingCode === 'MIN_LEVEL') {
    const level = label(job?.topikSpeakingLevelOptionItemId);
    const num = (level.match(/\d/) || [])[0];
    koreanDetails.push(
      num
        ? t(`TOPIK Speaking ${num}`, `TOPIK 말하기 ${num}`)
        : t('TOPIK Speaking required', 'TOPIK 말하기 필수'),
    );
  } else if (speakingCode === 'REQUIRED') {
    koreanDetails.push(t('TOPIK Speaking required', 'TOPIK 말하기 필수'));
  } else if (speakingCode === 'PREFERRED') {
    koreanDetails.push(t('TOPIK Speaking preferred', 'TOPIK 말하기 우대'));
  }

  const parts = [];
  if (koreanDetails.length) {
    parts.push(`${t('Korean', '한국어')}(${koreanDetails.join(', ')})`);
  }

  (job?.requiredLanguages || []).forEach((item) => {
    const optionCode = code(item?.optionItemId);
    if (optionCode === 'KOREAN') return; // 위 한국어 항목과 중복
    const optionLabel = label(item?.optionItemId);
    const name =
      optionCode === 'OTHER'
        ? item?.customText || optionLabel
        : optionLabel || item?.customText || '';
    if (name) parts.push(name);
  });

  return parts.join(' | ');
};

const JobOverview = ({ job, optionMeta }) => {
  const t = useT();
  const { lang } = useLang();
  const label = (id, fallback) =>
    getJobOptionLabel(optionMeta, id, fallback, lang);

  const careerLevel = label(job?.careerLevelOptionItemId);
  // Workplace Type: Onsite / Hybrid / Remote / Flexible 유형만 (세부 일수는 비노출)
  const workplaceType = label(job?.workingScheduleOptionItemId, '');
  const employmentType = label(job?.employmentTypeOptionItemId);

  // Salary: 유형 문구만 노출 (금액·통화 비노출)
  const salary = label(
    job?.salaryTypeOptionItemId,
    t('Not specified', '미지정'),
  );

  const baseAddress = (job?.companyAddress || '').trim();
  const detailAddress = (job?.companyAddressDetail || '').trim();
  const companyAddress =
    detailAddress &&
    detailAddress !== baseAddress &&
    !baseAddress.includes(detailAddress)
      ? `${baseAddress}, ${detailAddress}`
      : baseAddress;

  const languageSkills = buildLanguageSkills(job, optionMeta, lang, t);

  const locationEligibility = job?.koreaResidentsOnly
    ? t('Korea residents only', '국내 거주자만 지원 가능')
    : t('Overseas applicants accepted', '해외 거주자 지원 가능');

  return (
    <div>
      <div className='flex flex-col items-start py-[30px] w-full gap-2 tabletH:gap-4 text-text-primary'>
        <div className='typo-b18Strong self-stretch'>
          {t('Job Overview', '공고 개요')}
        </div>

        <div className='flex flex-col items-start gap-1.5 self-stretch'>
          <OverviewRow
            icon={<WorkerIcon />}
            label={t('Career Level', '경력')}
            value={careerLevel}
          />
          <OverviewRow
            icon={<WorkSpaceIcon />}
            label={t('Workplace Type', '근무 형태')}
            value={workplaceType || '-'}
          />
        </div>

        <div className='flex flex-col items-start gap-1.5 self-stretch'>
          <OverviewRow
            icon={<CashIcon />}
            label={t('Salary', '급여')}
            value={salary}
          />

          <OverviewRow
            icon={<BusinessIcon />}
            label={t('Employment Type', '고용 형태')}
            value={employmentType}
          />

          {job?.workTime && (
            <OverviewRow
              icon={<ClockIcon />}
              label={t('Work Time', '근무 시간')}
              value={job.workTime}
            />
          )}

          {job?.workingDays && (
            <OverviewRow
              icon={<ClockIcon />}
              label={t('Working Days', '근무 요일')}
              value={job.workingDays}
            />
          )}
        </div>

        <div className='flex flex-col items-start gap-1.5 self-stretch'>
          <OverviewRow
            icon={<SkillsIcon />}
            label={t('Language Skills', '언어 능력')}
            value={languageSkills || t('Not specified', '미지정')}
          />
          <OverviewRow
            icon={<LocationIcon className='w-5 h-5' />}
            label={t('Location', '지원 지역')}
            value={locationEligibility}
          />
          <OverviewRow
            icon={<OfficeIcon />}
            label={t('Work Location', '근무 위치')}
            value={companyAddress || '-'}
            copyIcon={companyAddress ? <CopyIcon /> : null}
          />
        </div>
      </div>

      <div className='border-b border-grayscale-gray02' />
    </div>
  );
};

export default JobOverview;
